import type { ArcadeGame } from '../../../game';
import type * as THREE from 'three';

export interface DeviceOrientationBinding {
  handler: (eventData: DeviceOrientationEvent) => void;
  dispose: () => void;
}

/**
 * Gyro: idle desktop camera sway + in-game ship tilt (L/R/U/D).
 * Mutates `mouse` in place (same as previous App-local handler).
 */
export function bindDeviceOrientation(deps: {
  getArcade: () => ArcadeGame | null;
  mouse: THREE.Vector2;
}): DeviceOrientationBinding {
  const handler = (eventData: DeviceOrientationEvent) => {
    const arcade = deps.getArcade();
    if (!arcade) return;

    const rawBeta = eventData.beta;
    const rawGamma = eventData.gamma;
    if (rawBeta == null || rawGamma == null) {
      return;
    }

    if (arcade.isPlaying()) {
      arcade.setOrientation(rawBeta, rawGamma);
      // Mild chase-cam lean from the same tilt (capped)
      deps.mouse.x = Math.max(-1, Math.min(1, rawGamma / 35));
      deps.mouse.y = Math.max(-1, Math.min(1, -(rawBeta - 55) / 40));
      return;
    }

    // Idle: subtle camera parallax from holding the phone
    const beta = Math.max(Math.min(rawBeta, 120), 0);
    if (beta > 85 && beta < 95) return;
    const yPos = (beta - 45) / 20;
    const xPos = rawGamma / 20;
    deps.mouse.x = xPos;
    deps.mouse.y = -yPos;
  };

  window.addEventListener('deviceorientation', handler, true);

  return {
    handler,
    dispose: () => {
      window.removeEventListener('deviceorientation', handler, true);
    },
  };
}
